import { Box, Popover, Stack } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { OptionButton } from './OptionButton.styles';
import { RangeHighlightDay } from './RangeHighlightDay.styles';
import { selectRangeDate, DateRange } from '../../../helpers';
import { TimePeriod } from '../../../types';

const PRESETS: { label: string; period: TimePeriod }[] = [
  { label: 'Last 7 Days', period: TimePeriod.LAST7DAYS },
  { label: 'Last 30 Days', period: TimePeriod.LAST30DAYS },
  { label: 'Last 90 Days', period: TimePeriod.LAST90DAYS },
  { label: 'Last 365 Days', period: TimePeriod.LAST365DAYS },
];

export default function DateRangePicker({
  open,
  anchorEl,
  onClose,
  timePeriod,
  startDateInterval,
  endDateInterval,
  onPresetChange,
  onCustomRangeChange,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  timePeriod: TimePeriod;
  startDateInterval: string | null;
  endDateInterval: string | null;
  onPresetChange: (period: TimePeriod) => void;
  onCustomRangeChange: (start: string, end: string) => void;
}) {
  const [range, setRange] = useState<DateRange>({
    start: startDateInterval,
    end: endDateInterval,
  });

  // Sync local selection whenever the committed range changes (e.g. preset).
  useEffect(() => {
    setRange({ start: startDateInterval, end: endDateInterval });
  }, [startDateInterval, endDateInterval, open]);

  const handleDayClick = (value: Dayjs | null) => {
    if (!value) return;
    const next = selectRangeDate(range, value.format('YYYY-MM-DD'));
    setRange(next);
    if (next.start && next.end) {
      onCustomRangeChange(next.start, next.end);
      onClose();
    }
  };

  const calendarValue = range.end
    ? dayjs(range.end)
    : range.start
      ? dayjs(range.start)
      : null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Stack direction="row">
        <Stack spacing={0.5} p={1.5} minWidth={150} borderRight={1} borderColor="divider">
          {PRESETS.map(preset => (
            <OptionButton
              key={preset.period}
              size="small"
              selected={timePeriod === preset.period}
              onClick={() => onPresetChange(preset.period)}
            >
              {preset.label}
            </OptionButton>
          ))}
        </Stack>
        <Box p={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={calendarValue}
              maxDate={dayjs()}
              onChange={handleDayClick}
              slots={{ day: RangeHighlightDay }}
              slotProps={{
                day: ((ownerState: { day: Dayjs }) => {
                  const day = ownerState.day;
                  const inRange = Boolean(
                    range.start &&
                      range.end &&
                      !day.isBefore(dayjs(range.start), 'day') &&
                      !day.isAfter(dayjs(range.end), 'day'),
                  );
                  return { inRange };
                }) as never,
              }}
            />
          </LocalizationProvider>
        </Box>
      </Stack>
    </Popover>
  );
}
